import OffcutStock from '../components/OffcutStock';
import ConversionCollection from '../components/ConversionCollection';
import {readConversionView,writeConversionView} from '../utils/conversionView';
import {bowRules,bowDestinationRule,applyBowRule,bowPlan,bowRequest} from '../utils/bowConversionForm';
import React, { useState, useEffect } from 'react';
import ConversionSearchSelect from '../components/ConversionSearchSelect';
import {loadConversionLists} from '../utils/conversionForm';
import YieldComparison from '../components/YieldComparison';
import BowConversions from '../components/BowConversions';
import HandleConversions from '../components/HandleConversions';
import { get, post, put } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import {calculate as calculateCutting} from '../utils/cuttingRules';

const STATUS_COLORS = {
  draft:                    { bg:'#F2EFE7', color:'#556B2F', label:'Draft' },
  submitted:                { bg:'#EDF4FF', color:'#1E4FAD', label:'Submitted' },
  accepted:                 { bg:'#FFF3DC', color:'#8B5E0A', label:'Accepted' },
  in_conversion:            { bg:'#FFF3DC', color:'#8B5E0A', label:'In Conversion' },
  returned_for_verification:{ bg:'#FDEAEA', color:'#8F2E24', label:'Awaiting Verification' },
  verified:                 { bg:'#E8F5EB', color:'#2E6B3A', label:'Verified' },
  completed:                { bg:'#E8F5EB', color:'#155724', label:'Completed' },
  clarification_required:   { bg:'#FDEAEA', color:'#8F2E24', label:'Clarification Required' },
  variance_review:          { bg:'#FDEAEA', color:'#8F2E24', label:'Variance Review' },
  cancelled:                { bg:'#F2EFE7', color:'#8A8A8A', label:'Cancelled' },
};

function previewCut(rule,raw){try{return rule?.cutting_config?calculateCutting(rule.cutting_config,raw||0):null;}catch(e){return {quantity:0,error:e.message};}}
function rawWasteFor(mcr,body){const c=mcr?.cutting_config;if(!c)return 0;if(c.type==='benchmark')return Number(body.raw_waste_quantity||0);const denominator=c.source_inches*(c.type==='sheet'?60:1);const cuts=(Number(body.actual_output_quantity||0)+Number(body.damaged_quantity||0)+Number(body.actual_waste_quantity||0))*c.piece_width_inches*c.piece_length_inches;const offcuts=(body.cutting_offcuts||[]).reduce((sum,o)=>sum+Number(o.width_inches||0)*Number(o.length_inches||0)*Number(o.quantity||0),0);return Math.round((Number(body.actual_raw_consumed||0)-(cuts+offcuts)/denominator)*1e6)/1e6;}
function CuttingLayout({plan}){return plan?<div style={{padding:12,background:'#F2EFE7',margin:'12px 0',borderRadius:8}}><strong>Planned output: {plan.quantity} pieces</strong><p>{plan.error||plan.method}</p>{(plan.layout||[]).map((r,i)=><div key={i}>{r.lanes} {r.orientation==='row'?'rows':'lanes'} × {r.pieces_per_lane} pieces; each {r.piece_width_inches} × {r.piece_length_inches} inches</div>)}<p>Planning output for the full batch. Record actual results, raw returns and any applicable measured offcuts after cutting.</p></div>:null;}

export default function MaterialConversionRequests() {
  const { user } = useAuth();
  const isInventory = ['super_admin','admin','inventory_officer'].includes(user?.role);
  const isProduction = ['super_admin','admin','production_team_lead'].includes(user?.role);

  const [view,setView]=useState('list');
  useEffect(()=>{setView(readConversionView(user?.id));},[user?.id]);
  const chooseView=next=>{setView(next);writeConversionView(user?.id,next);};
  const [mcrs, setMcrs] = useState([]);
  const [skus, setSkus] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [rules, setRules] = useState([]);
  const [bowCatalogue,setBowCatalogue]=useState([]),[bowLeads,setBowLeads]=useState([]),[bowRefresh,setBowRefresh]=useState(0),[formNotice,setFormNotice]=useState('');
  const [jobOrders, setJobOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // detail view
  const [modal, setModal] = useState(null); // create modal
  const [actionModal, setActionModal] = useState(null); // action modal
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');

  const [listErrors, setListErrors] = useState({});
  const [listsLoading, setListsLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    setListsLoading(true);
    setListErrors({});
    const setters = {requests:setMcrs, destinations:setSkus, rules:setRules, jobs:setJobOrders, parents:setMaterials};
    await Promise.all([loadConversionLists(get, (name, rows)=>setters[name](rows), (name,message)=>{
      setters[name]([]);
      setListErrors(previous=>({...previous,[name]:message}));
    }),get('/inventory-control/bow-conversions/options').then(o=>{
      if(!Array.isArray(o.catalogue)||!Array.isArray(o.leads))throw new Error('Bow setup response is incomplete');
      setBowCatalogue(o.catalogue);setBowLeads(o.leads);
    }).catch(e=>{setBowCatalogue([]);setBowLeads([]);setListErrors(previous=>({...previous,bows:e.message}));})]);
    setLoading(false);
    setListsLoading(false);
  };
  useEffect(() => { load(); }, []);

  const activeMaterials =
    materials.filter(
      material =>
        material.is_active !== false
    );

  const destinationSKUs =
    skus.filter(
      sku =>
        sku.inventory_type ===
          'prepared' &&
        sku.is_active !== false &&
        !/-STITCHED$|-HOSED$/.test(sku.sku) &&
        (!modal?.source_material_id || sku.material_id === modal.source_material_id)
    );

  const availableRules = rules.filter(rule => rule.is_active !== false &&
    activeMaterials.some(material=>material.id===rule.source_material_id) &&
    skus.some(sku=>sku.id===rule.destination_sku_id && sku.is_active!==false && sku.inventory_type==='prepared' && !/-STITCHED$|-HOSED$/.test(sku.sku)) &&
    (!modal?.source_material_id || rule.source_material_id===modal.source_material_id) &&
    (!modal?.destination_sku_id || rule.destination_sku_id===modal.destination_sku_id)).concat(bowRules(bowCatalogue).filter(rule=>
      (!modal?.source_material_id||rule.source_material_id===modal.source_material_id) &&
      (!modal?.destination_sku_id||rule.destinations.includes(modal.destination_sku_id))));
  const clearRule = {conversion_rule_id:'',cut_specification:'',expected_output_quantity:'',expected_waste_quantity:0};

  const selectedBowRule=bowDestinationRule(bowCatalogue,modal?.destination_sku_id);
  const currentBowPlan=selectedBowRule?bowPlan(selectedBowRule,modal?.raw_quantity_to_issue):null;
  const destinationOptions=[...destinationSKUs.map(sku=>({value:sku.id,label:`${sku.sku} — ${sku.description}`})),
    ...bowCatalogue.filter(c=>!modal?.source_material_id||c.satin_material_id===modal.source_material_id).map(c=>({value:'bow-product:'+c.finished_product_id,label:`${c.product_sku} — ${c.product_name} (Finished accessory — assembly)`}))];
  const chooseDestination=value=>{
    const bow=bowDestinationRule(bowCatalogue,value);
    if(bow){setModal(applyBowRule({...modal,destination_sku_id:value,assigned_to:modal.assigned_to||(bowLeads.length===1?bowLeads[0].id:'')},bow));return;}
    const sku=skus.find(x=>x.id===value);
    const candidates=rules.filter(r=>r.is_active!==false&&r.destination_sku_id===value&&r.source_material_id===sku?.material_id);
    const rule=candidates.length===1?candidates[0]:null;
    const expected=rule?(previewCut(rule,modal.raw_quantity_to_issue)?.quantity??Math.floor(Number(modal.raw_quantity_to_issue||0)*Number(rule.expected_yield))):'';
    setModal({...modal,...clearRule,destination_sku_id:value,source_material_id:sku?.material_id||modal.source_material_id,
      ...(rule?{conversion_rule_id:rule.id,cut_specification:rule.cut_specification||'',expected_output_quantity:String(expected),expected_waste_quantity:rule.cutting_config?0:expected*Number(rule.allowable_waste_percent||0)/100}:{})});
  };

  const filtered = mcrs.filter(m => {
    const matchStatus = !statusFilter || m.status === statusFilter;
    const matchSearch = !search ||
      m.mcr_number?.toLowerCase().includes(search.toLowerCase()) ||
      m.source_description?.toLowerCase().includes(search.toLowerCase()) ||
      m.dest_description?.toLowerCase().includes(search.toLowerCase());
    return matchStatus && matchSearch;
  });

  // When rule is selected, auto-fill expected output
  const onRuleSelect = (ruleId) => {
    if (!ruleId) {if(!selectedBowRule)setModal({...modal,...clearRule});return;}
    const rule = availableRules.find(r => r.id === ruleId);
    if(rule?.kind==='bow'){setModal(applyBowRule({...modal,assigned_to:modal.assigned_to||(bowLeads.length===1?bowLeads[0].id:'')},rule));return;}
    if (rule && modal) {
      const expectedOutput = previewCut(rule,modal.raw_quantity_to_issue)?.quantity ?? Math.floor(parseFloat(modal.raw_quantity_to_issue || 0) * parseFloat(rule.expected_yield || 0));
      const expectedWaste = rule.cutting_config ? 0 : expectedOutput * parseFloat(rule.allowable_waste_percent || 0) / 100;
      setModal({
        ...modal,
        conversion_rule_id: ruleId,
        source_material_id: rule.source_material_id,
        destination_sku_id: rule.destination_sku_id,
        cut_specification: rule.cut_specification || '',
        expected_output_quantity: expectedOutput.toFixed(0),
        expected_waste_quantity: expectedWaste.toFixed(0)
      });
    }
  };

  const onQtyChange = (qty) => {
    const rule = rules.find(r => r.id === modal?.conversion_rule_id);
    if (rule) {
      const expectedOutput = previewCut(rule,qty)?.quantity ?? Math.floor(parseFloat(qty || 0) * parseFloat(rule.expected_yield || 0));
      const expectedWaste = rule.cutting_config ? 0 : expectedOutput * parseFloat(rule.allowable_waste_percent || 0) / 100;
      setModal({
        ...modal,
        raw_quantity_to_issue: qty,
        expected_output_quantity: expectedOutput.toFixed(0),
        expected_waste_quantity: expectedWaste.toFixed(0)
      });
    } else {
      setModal({ ...modal, raw_quantity_to_issue: qty });
    }
  };

  const openCreate = () => {
    setError('');
    setModal({
      conversion_rule_id: '', source_material_id: '', destination_sku_id: '',
      raw_quantity_to_issue: '', expected_output_quantity: '',
      expected_waste_quantity: 0, cut_specification: '',
      required_completion_date: '', reason: '',
      related_job_order_id: '', assigned_to: '', action_key:crypto.randomUUID()
    });
  };

  const saveMCR = async () => {
    setError('');
    if(selectedBowRule){
      if(listsLoading||listErrors.bows){setError('Reload the bow setup before saving.');return;}
      if(!modal.assigned_to||!modal.raw_quantity_to_issue||String(modal.reason||'').trim().length<10){setError('Select a Production Team Lead, enter quantity and a reason of at least 10 characters.');return;}
      setSaving(true);
      try{const saved=await post('/inventory-control/bow-conversions',bowRequest(selectedBowRule,modal));setModal(null);setBowRefresh(n=>n+1);setFormNotice(`${saved.data?.request_number||'Bow batch'} created. Inputs are reserved; follow acceptance, production reporting and inventory verification in Detachable bow production above.`);}
      catch(e){setError(e.message);}finally{setSaving(false);}return;
    }
    if(listErrors.bows){setError('Reload the bow setup before creating a conversion request.');return;}
    if (!modal.source_material_id || !modal.destination_sku_id || !modal.raw_quantity_to_issue || !modal.reason) {
      setError('Parent material, destination SKU, quantity and reason are required'); return;
    }
    if (listsLoading || listErrors.parents || listErrors.destinations || listErrors.rules) {setError('Wait for the material lists to load, or retry the failed lists.');return;}
    if (!activeMaterials.some(m=>m.id===modal.source_material_id) || !destinationSKUs.some(s=>s.id===modal.destination_sku_id)) {setError('Select an active parent and a destination belonging to that parent.');return;}
    if (modal.conversion_rule_id && !availableRules.some(r=>r.id===modal.conversion_rule_id)) {setError('Select a rule matching the selected materials.');return;}
    setSaving(true);
    try {
      await post('/inventory-control/mcr', modal);
      setModal(null);
      load();
    } catch(e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const submitMCR = async (id) => {
    if (!window.confirm('Submit this MCR? This will reserve the raw material for conversion.')) return;
    setSaving(true);
    try {
      await put('/inventory-control/mcr/'+id+'/submit', {});
      load();
      setSelected(null);
    } catch(e) { alert(e.message); }
    finally { setSaving(false); }
  };

  const acceptMCR = async (id) => {
    if (!window.confirm('Accept this MCR and take custody of the raw material for conversion?')) return;
    setSaving(true);
    try {
      await put('/inventory-control/mcr/'+id+'/accept', {});
      load();
      setSelected(null);
    } catch(e) { alert(e.message); }
    finally { setSaving(false); }
  };

  const openCompleteConversion = (mcr) => {
    setError('');
    setActionModal({
      type: 'complete',
      mcr,
      actual_raw_consumed: Number(mcr.raw_quantity_to_issue),
      actual_output_quantity: Number(mcr.expected_output_quantity),
      actual_waste_quantity: Number(mcr.expected_waste_quantity || 0),
      damaged_quantity: 0,
      remaining_raw_returned: 0,
      production_notes: '', cutting_offcuts: [], raw_reconciliation_confirmed: false
    });
  };

  const openVerify = (mcr) => {
    setError('');
    setActionModal({
      type: 'verify',
      mcr,
      inventory_verified_quantity: Number(mcr.actual_output_quantity ?? mcr.expected_output_quantity),
      variance_reason: ''
    });
  };

  const completeConversion = async () => {
    setError('');
    setSaving(true);
    try {
      await put('/inventory-control/mcr/'+actionModal.mcr.id+'/complete-conversion', {...actionModal, raw_waste_quantity:rawWasteFor(actionModal.mcr,actionModal)});
      setActionModal(null);
      load();
    } catch(e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const verifyConversion = async () => {
    setError('');
    const verified = parseFloat(actionModal.inventory_verified_quantity);
    const expected = parseFloat(actionModal.mcr.actual_output_quantity ?? actionModal.mcr.expected_output_quantity);
    const variance = verified - expected;
    if (Math.abs(variance) > 0 && !actionModal.variance_reason) {
      setError('Please provide a reason for the variance'); return;
    }
    setSaving(true);
    try {
      await put('/inventory-control/mcr/'+actionModal.mcr.id+'/verify', actionModal);
      setActionModal(null);
      load();
    } catch(e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const renderActions=m=>(<div style={{display:'flex',gap:4,flexWrap:'wrap'}}>
                        <button className="btn btn-ghost btn-sm"
                          onClick={async()=>{ const r=await get('/inventory-control/mcr/'+m.id); setSelected(r.data); }}>
                          View
                        </button>
                        {isInventory && m.status==='draft' && (
                          <button className="btn btn-primary btn-sm" onClick={()=>submitMCR(m.id)}>
                            Submit
                          </button>
                        )}
                        {isProduction && m.status==='submitted' && (
                          <button className="btn btn-primary btn-sm"
                            style={{background:'#8B5E0A',border:'none'}} onClick={()=>acceptMCR(m.id)}>
                            Accept
                          </button>
                        )}
                        {isProduction && m.status==='accepted' && (
                          <button className="btn btn-primary btn-sm"
                            style={{background:'#556B2F',border:'none'}} onClick={()=>openCompleteConversion(m)}>
                            Complete
                          </button>
                        )}
                        {isInventory && m.status==='returned_for_verification' && (
                          <button className="btn btn-primary btn-sm"
                            style={{background:'#2E6B3A',border:'none'}} onClick={()=>openVerify(m)}>
                            Verify
                          </button>
                        )}
                      </div>);
  const conversionFields=[
    {key:'number',label:'MCR #',render:m=><strong>{m.mcr_number}</strong>},
    {key:'source',label:'From (Raw)',render:m=><>{m.source_description}<div className="text-muted text-sm">{m.source_sku}</div></>},
    {key:'destination',label:'To (Prepared)',render:m=><>{m.dest_description}<div className="text-muted text-sm">{m.dest_sku}</div></>},
    {key:'raw',label:'Raw quantity',render:m=><>{Number(m.raw_quantity_to_issue).toLocaleString()} {m.source_unit}</>},
    {key:'expected',label:'Expected / actual output',render:m=><>{Number(m.expected_output_quantity).toLocaleString()} {m.dest_unit}<div>Reported: {m.actual_output_quantity==null?'pending':Number(m.actual_output_quantity)}; verified: {m.inventory_verified_quantity==null?'pending':Number(m.inventory_verified_quantity)}</div></>},
    {key:'status',label:'Status',render:m=>{const status=STATUS_COLORS[m.status]||STATUS_COLORS.draft;return <span style={{background:status.bg,color:status.color,padding:'3px 8px',borderRadius:12,fontWeight:700}}>{status.label}</span>;}},
    {key:'lead',label:'Assigned to',render:m=>m.assigned_to_name||'—'},
    {key:'date',label:'Date',render:m=>new Date(m.created_at).toLocaleDateString('en-NG')}
  ];

  // Status counts
  const counts = {};
  mcrs.forEach(m => { counts[m.status] = (counts[m.status]||0) + 1; });
  const actionRequired = mcrs.filter(m =>
    ['submitted','returned_for_verification'].includes(m.status)
  ).length;

  return (
    <div>
      {!modal && !listsLoading && Object.keys(listErrors).length>0 && <div className="alert alert-error" role="alert">
        {Object.entries(listErrors).map(([name,message])=><div key={name}>Could not load {name}: {message}</div>)}
        <button type="button" className="btn btn-outline btn-sm" onClick={load}>Retry loading lists</button>
      </div>}
      {formNotice&&<div className="alert alert-success" role="status">{formNotice}</div>}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:12,flexWrap:'wrap',marginBottom:20}}>
        <strong>Conversion requests</strong>
        <div role="group" aria-label="Conversion request view" style={{display:'flex',gap:6}}>
          <button type="button" className={'btn btn-sm '+(view==='list'?'btn-primary':'btn-outline')} aria-pressed={view==='list'} onClick={()=>chooseView('list')}>List view</button>
          <button type="button" className={'btn btn-sm '+(view==='cards'?'btn-primary':'btn-outline')} aria-pressed={view==='cards'} onClick={()=>chooseView('cards')}>Cards view</button>
        </div>
      </div>
      <OffcutStock view={view} />
      <BowConversions refreshToken={bowRefresh} view={view} />
      <HandleConversions view={view} />
      <div className="flex-between mb-lg">
        <div>
          <h2 className="page-title">Material Conversion Requests</h2>
          <p className="text-muted text-sm" style={{marginTop:4}}>
            Convert raw materials (rolls, yards) into prepared production units (pieces, sets)
          </p>
        </div>
        {isInventory && (
          <button className="btn btn-primary" onClick={openCreate}>+ New MCR</button>
        )}
      </div>

      {/* Action required alert */}
      {actionRequired > 0 && (
        <div style={{background:'#FFF3DC',border:'1px solid #F0D9A0',borderRadius:8,
          padding:'10px 16px',marginBottom:16,fontSize:12,color:'#8B5E0A'}}>
          ⚠ <strong>{actionRequired}</strong> conversion request{actionRequired!==1?'s':''} require action
        </div>
      )}

      {/* Status filter pills */}
      <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:16}}>
        <button className={'btn btn-sm '+(statusFilter===''?'btn-primary':'btn-outline')}
          onClick={()=>setStatusFilter('')}>All ({mcrs.length})</button>
        {Object.entries(counts).map(([status, count]) => {
          const sc = STATUS_COLORS[status] || STATUS_COLORS.draft;
          return (
            <button key={status}
              className={'btn btn-sm '+(statusFilter===status?'btn-primary':'btn-outline')}
              onClick={()=>setStatusFilter(statusFilter===status?'':status)}
              style={statusFilter===status?{}:{borderColor:sc.color,color:sc.color}}>
              {sc.label} ({count})
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="search-bar" style={{marginBottom:16}}>
        <input placeholder="Search MCR number or material..."
          value={search} onChange={e=>setSearch(e.target.value)} style={{flex:1}} />
        <button className="btn btn-outline btn-sm" onClick={load}>↻ Refresh</button>
      </div>

      <ConversionCollection title="Material Conversion Requests" view={view} rows={filtered} fields={conversionFields} renderActions={renderActions} rowKey={m=>m.id} loading={loading}
        empty={<><h3>No conversion requests</h3>{isInventory&&<button className="btn btn-primary" onClick={openCreate}>Create First MCR</button>}</>} />

      {/* Detail Panel */}
      {selected && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setSelected(null)}>
          <div className="modal" style={{maxWidth:620}}>
            <div className="modal-hdr">
              <span className="modal-title">{selected.mcr_number}</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setSelected(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:16}}>
                {[
                  ['Status', <span style={{...STATUS_COLORS[selected.status],fontSize:11,fontWeight:700,padding:'2px 8px',borderRadius:20}}>{STATUS_COLORS[selected.status]?.label}</span>],
                  ['Created By', selected.created_by_name],
                  ['Source', `${selected.source_description} (${selected.source_sku})`],
                  ['Destination', `${selected.dest_description} (${selected.dest_sku})`],
                  ['Raw Qty to Issue', `${Number(selected.raw_quantity_to_issue).toLocaleString()} ${selected.source_unit}`],
                  ['Expected Output', `${Number(selected.expected_output_quantity).toLocaleString()} ${selected.dest_unit}`],
                  ['Expected Waste', selected.expected_waste_quantity > 0 ? Number(selected.expected_waste_quantity).toLocaleString() : '—'],
                  ['Cut Specification', selected.cut_specification||'—'],
                  ['Required By', selected.required_completion_date||'—'],
                  ['Reason', selected.reason],
                ].map(([label, value]) => (
                  <div key={label}>
                    <div style={{fontSize:10,color:'#8A8A8A',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:2}}>{label}</div>
                    <div style={{fontSize:13,color:'#3B2A21',fontWeight:500}}>{value}</div>
                  </div>
                ))}
              </div>

              <CuttingLayout plan={selected.cutting_plan} />
              <YieldComparison record={selected} type="mcr" onRefresh={()=>{setSelected(null);load();}}/>
              {selected.cutting_config && selected.raw_waste_quantity!==null && <div style={{padding:12,marginBottom:12}}>
                <strong>Raw waste: {Number(selected.raw_waste_quantity)} {selected.cutting_config.source_unit}</strong>
                {(selected.cutting_offcuts||[]).map((o,i)=><p key={i}>Reusable offcut: {o.quantity} × {o.width_inches} × {o.length_inches} inches; {selected.status==='completed'?'verified and recorded':'awaiting verification'}</p>)}
              </div>}
              {/* Conversion results if completed */}
              {selected.actual_output_quantity != null && (
                <div style={{background:'#E8F5EB',borderRadius:8,padding:14,marginBottom:16}}>
                  <div style={{fontWeight:700,fontSize:12,color:'#2E6B3A',marginBottom:10}}>Conversion Results</div>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10}}>
                    {[
                      ['Raw Consumed', `${Number(selected.actual_raw_consumed||0).toLocaleString()} ${selected.source_unit}`],
                      ['Output Produced', `${Number(selected.actual_output_quantity||0).toLocaleString()} ${selected.dest_unit}`],
                      ['Waste', `${Number(selected.actual_waste_quantity||0).toLocaleString()}`],
                      ['Verified Qty', selected.inventory_verified_quantity ? Number(selected.inventory_verified_quantity).toLocaleString() : '—'],
                      ['Verification difference', selected.inventory_variance != null ? (selected.inventory_variance > 0 ? '+':'')+Number(selected.inventory_variance).toLocaleString() : '—'],
                      ['Verification difference reason', selected.variance_reason||'—'],
                    ].map(([l,v])=>(
                      <div key={l}>
                        <div style={{fontSize:10,color:'#8A8A8A',marginBottom:2}}>{l}</div>
                        <div style={{fontSize:13,fontWeight:600,color:'#3B2A21'}}>{v}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Status history */}
              {selected.history?.length > 0 && (
                <div>
                  <div style={{fontSize:11,fontWeight:700,color:'#3B2A21',textTransform:'uppercase',
                    letterSpacing:'.06em',marginBottom:8}}>Status History</div>
                  {selected.history.map((h,i) => (
                    <div key={i} style={{display:'flex',gap:10,alignItems:'flex-start',
                      padding:'6px 0',borderBottom:'1px solid #F2EFE7'}}>
                      <div style={{fontSize:10,color:'#8A8A8A',whiteSpace:'nowrap',minWidth:120}}>
                        {new Date(h.created_at).toLocaleString('en-NG',{day:'numeric',month:'short',hour:'2-digit',minute:'2-digit'})}
                      </div>
                      <div style={{flex:1}}>
                        <span style={{fontSize:11,fontWeight:600,color:'#3B2A21'}}>{h.changed_by_name}</span>
                        <span style={{fontSize:11,color:'#8A8A8A'}}> → {STATUS_COLORS[h.to_status]?.label||h.to_status}</span>
                        {h.notes && <div style={{fontSize:11,color:'#555',marginTop:2}}>{h.notes}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setSelected(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Create MCR Modal */}
      {modal && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setModal(null)}>
          <div className="modal" style={{maxWidth:560}}>
            <div className="modal-hdr">
              <span className="modal-title">New Material Conversion Request</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              {error && <div className="alert alert-error">{error}</div>}

              {(listsLoading || Object.keys(listErrors).length>0) && <div role="status" className="alert alert-error">
                {listsLoading ? 'Loading material lists…' : Object.entries(listErrors).map(([name,message])=><div key={name}>Could not load {name}: {message}</div>)}
                {!listsLoading && <button type="button" className="btn btn-outline btn-sm" onClick={load}>Retry loading lists</button>}
              </div>}
              <div className="form-group">
                <label className="form-label">{selectedBowRule?.stage==='assemble'?'Bow colour source (satin is not issued during assembly)':'Parent Material (Raw Stock) *'}</label>
                <ConversionSearchSelect label="Parent Material" value={modal.source_material_id}
                  disabled={listsLoading || Boolean(listErrors.parents)} placeholder="Type a name, colour or material code…"
                  options={activeMaterials.map(m=>({value:m.id,label:`${m.material_code || ''} — ${m.name} (Stock: ${Number(m.current_stock||0).toLocaleString()} ${m.unit_of_measure})`}))}
                  onChange={value=>setModal({...modal,...clearRule,source_material_id:value,destination_sku_id:'',raw_quantity_to_issue:'',action_key:crypto.randomUUID()})}/>
              </div>
              <div className="form-group">
                <label className="form-label">Destination Material / Finished Bow *</label>
                <ConversionSearchSelect label="Destination Material" value={modal.destination_sku_id}
                  disabled={listsLoading || Boolean(listErrors.destinations) || Boolean(listErrors.bows)}
                  placeholder="Type a name, colour, size or SKU…"
                  emptyMessage="No active prepared SKUs belong to this parent material."
                  options={destinationOptions}
                  onChange={chooseDestination}/>
              </div>
              <div className="form-group">
                <label className="form-label">Conversion Rule (auto-loaded for bows)</label>
                <ConversionSearchSelect label="Conversion Rule" value={modal.conversion_rule_id}
                  disabled={listsLoading || Boolean(listErrors.rules)} placeholder="Type material, SKU or cutting specification…"
                  emptyMessage="No active rule matches these materials."
                  options={availableRules.map(r=>({value:r.id,label:r.kind==='bow'?r.label:`${r.source_description} → ${r.dest_sku || ''} ${r.dest_description} — ${r.cut_specification || ''} (${r.expected_yield} ${r.dest_unit} per ${r.source_unit})`}))}
                  onChange={onRuleSelect}/>
              </div>

              {selectedBowRule&&<div style={{padding:12,background:'#F2EFE7',borderRadius:8,marginBottom:12}} role="status">
                <strong>{selectedBowRule.label}</strong>
                <p>{selectedBowRule.stage==='cut'?'One satin issue produces both components. Actual body and taper counts are reported and verified separately.':'Assembly consumes prepared bodies, tapers, clips and hemming gum. No satin is issued at this stage.'}</p>
                {currentBowPlan.inputs.map((i,index)=><div key={index}>Input: {i.quantity} {i.unit} — {i.label}</div>)}
                {currentBowPlan.outputs.map((o,index)=><div key={index}>Expected output: {o.quantity} pieces — {o.label}</div>)}
                <label className="form-label">Production Team Lead *</label><ConversionSearchSelect label="Bow Production Team Lead" value={modal.assigned_to} options={bowLeads.map(l=>({value:l.id,label:l.name}))} placeholder="Select lead…" onChange={value=>setModal({...modal,assigned_to:value})}/>
              </div>}
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">{selectedBowRule?(selectedBowRule.stage==='cut'?'Satin yards to issue *':'Number of finished bows *'):'Raw Quantity to Issue *'}</label>
                  <input type="number" className="form-control" value={modal.raw_quantity_to_issue||''}
                    onChange={e=>onQtyChange(e.target.value)}
                    placeholder="e.g. 5" min="0" step={selectedBowRule?.stage==='assemble'?'1':'0.001'} />
                </div>
                <div className="form-group">
                  <label className="form-label">Expected Output Quantity</label>
                  <input type="number" className="form-control" readOnly={Boolean(selectedBowRule||rules.find(r=>r.id===modal.conversion_rule_id)?.cutting_config)} value={selectedBowRule?currentBowPlan.quantity:(modal.expected_output_quantity??'')}
                    onChange={e=>setModal({...modal,expected_output_quantity:e.target.value})}
                    placeholder="Auto-calculated from rule" min="0" />
                </div>
              </div>

              {!selectedBowRule&&<>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Expected Waste</label>
                  <input type="number" className="form-control" readOnly={Boolean(rules.find(r=>r.id===modal.conversion_rule_id)?.cutting_config)} value={modal.expected_waste_quantity||0}
                    onChange={e=>setModal({...modal,expected_waste_quantity:e.target.value})}
                    min="0" step="0.01" />
                </div>
                <div className="form-group">
                  <label className="form-label">Required Completion Date</label>
                  <input type="date" className="form-control" value={modal.required_completion_date||''}
                    onChange={e=>setModal({...modal,required_completion_date:e.target.value})} />
                </div>
              </div>

              <CuttingLayout plan={previewCut(rules.find(r=>r.id===modal.conversion_rule_id),modal.raw_quantity_to_issue)} />
              <div className="form-group">
                <label className="form-label">Cut Specification</label>
                <input className="form-control" value={modal.cut_specification||''}
                  onChange={e=>setModal({...modal,cut_specification:e.target.value})}
                  placeholder="e.g. 26 inches per piece" />
              </div>

              <div className="form-group">
                <label className="form-label">Related Job Order</label>
                <select className="form-control" value={modal.related_job_order_id||''}
                  onChange={e=>setModal({...modal,related_job_order_id:e.target.value})}>
                  <option value="">None / Stock replenishment</option>
                  {jobOrders
                  .filter(jo =>
                    [
                      'pending',
                      'ready'
                    ].includes(jo.status)
                  )
                  .map(jo=>(
                    <option key={jo.id} value={jo.id}>
                      {jo.job_number} — {jo.customer_name||''}
                    </option>
                  ))}
                </select>
              </div>

              </>}
              <div className="form-group">
                <label className="form-label">Reason *</label>
                <select className="form-control" value={modal.reason||''}
                  onChange={e=>setModal({...modal,reason:e.target.value})}>
                  <option value="">Select reason...</option>
                  <option value="Replenish prepared stock">Replenish prepared stock</option>
                  <option value="Job Order requirement">Job Order requirement</option>
                  <option value="Emergency production requirement">Emergency production requirement</option>
                  <option value="Stock preparation">Stock preparation</option>
                </select>
              </div>
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveMCR} disabled={saving}>
                {saving?'Saving...':selectedBowRule?'Create Bow Conversion':'Create MCR'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Action Modal — Complete Conversion */}
      {actionModal?.type === 'complete' && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setActionModal(null)}>
          <div className="modal" style={{maxWidth:500}}>
            <div className="modal-hdr">
              <span className="modal-title">Complete Conversion — {actionModal.mcr.mcr_number}</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setActionModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              {error && <div className="alert alert-error">{error}</div>}
              <div style={{background:'#F9F8F5',borderRadius:8,padding:12,marginBottom:16,fontSize:12,color:'#555'}}>
                Record the actual results of the conversion. Inventory will physically verify quantities before stock is updated.
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Actual Raw Consumed</label>
                  <input type="number" className="form-control" value={actionModal.actual_raw_consumed??''}
                    onChange={e=>setActionModal({...actionModal,actual_raw_consumed:e.target.value})}
                    min="0" step="0.01" />
                </div>
                <div className="form-group">
                  <label className="form-label">Usable Pieces Produced *</label>
                  <input type="number" className="form-control" value={actionModal.actual_output_quantity??''}
                    onChange={e=>setActionModal({...actionModal,actual_output_quantity:e.target.value})}
                    min="0" autoFocus />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Rejected Cut Pieces (pcs)</label>
                  <input type="number" className="form-control" value={actionModal.actual_waste_quantity||0}
                    onChange={e=>setActionModal({...actionModal,actual_waste_quantity:e.target.value})}
                    min="0" step="0.01" />
                </div>
                <div className="form-group">
                  <label className="form-label">Damaged Pieces</label>
                  <input type="number" className="form-control" value={actionModal.damaged_quantity||0}
                    onChange={e=>setActionModal({...actionModal,damaged_quantity:e.target.value})}
                    min="0" />
                </div>
              </div>

              <div style={{padding:12,background:'#F2EFE7',marginBottom:12}}><strong>Planned good pieces: {Number(actionModal.mcr.expected_output_quantity||0)}</strong><p>Actual good pieces: {Number(actionModal.actual_output_quantity||0)}; variance: {Number(actionModal.actual_output_quantity||0)-Number(actionModal.mcr.expected_output_quantity||0)}. Explain any difference in production notes.</p></div>
              {actionModal.mcr.cutting_config?.type==='benchmark' && <div style={{padding:12,background:'#F2EFE7',marginBottom:12}}><h4>Cutting raw reconciliation</h4><p>{actionModal.mcr.cutting_config.benchmark_kind==='hose' ? `Cut hose to ${actionModal.mcr.cutting_config.cut_length_inches} inches. Planning uses an average of 82 yards per roll; actual roll length and output can differ.` : 'Cut each ribbon strip to 24 inches. Ten strips per roll is a planning benchmark.'} Enter actual good, damaged and rejected pieces. Record consumed rolls, unused rolls returned and raw waste separately. Explain any yield difference from plan.</p><label>Raw waste within consumed rolls<input type="number" min="0" step="0.001" className="form-control" value={actionModal.raw_waste_quantity||0} onChange={e=>setActionModal({...actionModal,raw_waste_quantity:e.target.value,raw_reconciliation_confirmed:false})}/></label><label><input type="checkbox" checked={Boolean(actionModal.raw_reconciliation_confirmed)} onChange={e=>setActionModal({...actionModal,raw_reconciliation_confirmed:e.target.checked})}/> I reconciled consumed material, returns and raw waste and counted actual good, damaged and rejected lengths.</label></div>}
              {actionModal.mcr.cutting_config && actionModal.mcr.cutting_config.type!=='benchmark' && <div style={{padding:12,background:'#F2EFE7',marginBottom:12}}>
                <h4>Reusable offcuts</h4>
                <p>Enter measured offcuts separately. They are recorded after Inventory verification and are not added back as full-width yards. For webbing and zipper tape use width 1 and the actual leftover length in inches.</p>
                {(actionModal.cutting_offcuts||[]).map((o,index)=><div key={index} style={{borderBottom:'1px solid #ccc',paddingBottom:8}}>
                  {[['Width (inches)','width_inches'],['Length (inches)','length_inches'],['Number of offcuts','quantity']].map(([label,key])=><label key={key} style={{display:'inline-block',maxWidth:130,marginRight:6}}>{label}<input type="number" min="0" step={key==='quantity'?'1':'0.01'} className="form-control" value={o[key]} onChange={e=>setActionModal({...actionModal,raw_reconciliation_confirmed:false,cutting_offcuts:actionModal.cutting_offcuts.map((v,j)=>j===index?{...v,[key]:e.target.value}:v)})}/></label>)}
                  <button className="btn btn-ghost btn-sm" onClick={()=>setActionModal({...actionModal,raw_reconciliation_confirmed:false,cutting_offcuts:actionModal.cutting_offcuts.filter((_,j)=>j!==index)})}>Remove</button>
                </div>)}
                <button className="btn btn-outline btn-sm" onClick={()=>setActionModal({...actionModal,raw_reconciliation_confirmed:false,cutting_offcuts:[...(actionModal.cutting_offcuts||[]),{width_inches:actionModal.mcr.cutting_config.type==='linear'?1:'',length_inches:'',quantity:1}]})}>Add measured offcut</button>
                <p><strong>Raw waste: {rawWasteFor(actionModal.mcr,actionModal)} {actionModal.mcr.cutting_config.source_unit}</strong> — consumed material less all cut pieces and reusable offcuts. A negative figure must be corrected.</p>
                <label><input type="checkbox" checked={Boolean(actionModal.raw_reconciliation_confirmed)} onChange={e=>setActionModal({...actionModal,raw_reconciliation_confirmed:e.target.checked})}/> I have recorded reusable offcuts and confirm the remaining raw material is waste.</label>
              </div>}
              <div className="form-group">
                <label className="form-label">Remaining Raw Material Returned</label>
                <input type="number" className="form-control" value={actionModal.remaining_raw_returned||0}
                  onChange={e=>setActionModal({...actionModal,remaining_raw_returned:e.target.value})}
                  min="0" step="0.01" />
              </div>

              <div className="form-group">
                <label className="form-label">Production notes / yield variance explanation</label>
                <textarea className="form-control" rows={2} value={actionModal.production_notes||''}
                  onChange={e=>setActionModal({...actionModal,production_notes:e.target.value})}
                  placeholder="Any notes about the conversion..." />
              </div>
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setActionModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={completeConversion} disabled={saving}
                style={{background:'#556B2F',border:'none'}}>
                {saving?'Saving...':'Return to Inventory for Verification'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Action Modal — Verify Conversion */}
      {actionModal?.type === 'verify' && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setActionModal(null)}>
          <div className="modal" style={{maxWidth:480}}>
            <div className="modal-hdr">
              <span className="modal-title">Verify Conversion — {actionModal.mcr.mcr_number}</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setActionModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              {error && <div className="alert alert-error">{error}</div>}

              <div style={{background:'#FFF3DC',border:'1px solid #F0D9A0',borderRadius:8,
                padding:12,marginBottom:16,fontSize:12,color:'#8B5E0A'}}>
                ⚠ <strong>Physical count required.</strong> Count the returned pieces yourself
                before entering the verified quantity. Do not rely on what Production reported.
              </div>

              <div style={{background:'#F9F8F5',borderRadius:8,padding:12,marginBottom:16}}>
                <div style={{fontSize:12,color:'#555'}}>
                  Production reported: <strong>{Number(actionModal.mcr.actual_output_quantity||0).toLocaleString()}</strong> pieces
                </div>
                <div style={{fontSize:12,color:'#555',marginTop:4}}>
                  Expected output: <strong>{Number(actionModal.mcr.expected_output_quantity||0).toLocaleString()}</strong> pieces
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Inventory Verified Quantity (physical count) *</label>
                <input type="number" className="form-control" value={actionModal.inventory_verified_quantity||''}
                  onChange={e=>setActionModal({...actionModal,inventory_verified_quantity:e.target.value})}
                  min="0" autoFocus />
              </div>

              {actionModal.inventory_verified_quantity && (
                (() => {
                  const v = parseFloat(actionModal.inventory_verified_quantity);
                  const e = parseFloat(actionModal.mcr.actual_output_quantity ?? actionModal.mcr.expected_output_quantity);
                  const diff = v - e;
                  return diff !== 0 ? (
                    <div style={{background:diff<0?'#FDEAEA':'#FFF3DC',borderRadius:8,
                      padding:'10px 14px',marginBottom:14,fontSize:12,
                      color:diff<0?'#8F2E24':'#8B5E0A',fontWeight:600}}>
                      {diff<0?`⚠ Shortage: ${Math.abs(diff).toLocaleString()} pieces less than reported`
                             :`⚠ Excess: ${diff.toLocaleString()} pieces more than reported`}
                    </div>
                  ) : (
                    <div style={{background:'#E8F5EB',borderRadius:8,padding:'10px 14px',
                      marginBottom:14,fontSize:12,color:'#2E6B3A',fontWeight:600}}>
                      ✅ Quantity matches production report
                    </div>
                  );
                })()
              )}

              {parseFloat(actionModal.inventory_verified_quantity||0) !==
               parseFloat(actionModal.mcr.actual_output_quantity ?? actionModal.mcr.expected_output_quantity) && (
                <div className="form-group">
                  <label className="form-label">Variance Reason *</label>
                  <textarea className="form-control" rows={2} value={actionModal.variance_reason||''}
                    onChange={e=>setActionModal({...actionModal,variance_reason:e.target.value})}
                    placeholder="Explain the difference between reported and verified quantity..." />
                </div>
              )}

              <div style={{background:'#FDEAEA',borderRadius:8,padding:12,fontSize:12,color:'#8F2E24'}}>
                ⚠ Confirming verification will <strong>permanently update stock balances</strong> —
                raw material was deducted at acceptance; verified prepared pieces will now be added, and unused raw material will be returned.
              </div>
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setActionModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={verifyConversion} disabled={saving}
                style={{background:'#2E6B3A',border:'none'}}>
                {saving?'Verifying...':'✅ Confirm & Update Stock'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
